import type { ComponentChildren } from "preact";
import { useEffect, useMemo, useState } from "preact/hooks";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  countReferenceStates,
  filterIssues,
  getRenderWindow,
  retainedFieldCount,
  sortDocuments,
} from "./domain/collaboration";
import type { ImportedIfcModel } from "./domain/ifcModelImport";
import type {
  Completeness,
  CollaborationFixture,
  ElementReference,
  IssueRecord,
  ModelReference,
  ReferenceState,
} from "./domain/types";
import {
  approvalStatuses,
  issuePriorities,
  rfiStatuses,
  type ApprovalRow,
  type DocumentVersionRow,
  type IssuePriority,
  type RfiRow,
  type ShadowIssueRow,
} from "./c1/shadowFoundation/types";
import { loadIssueDraft, loadUiState, saveIssueDraft, saveUiState, type Page } from "./browserStorage";
import { c0Fixture } from "./fixtures/c0";
import {
  defaultIdentityClient,
  defaultLiveApprovalsClient,
  defaultLiveDocumentsClient,
  defaultLiveIssuesClient,
  defaultLiveRfisClient,
  type IdentityClient,
  type IdentitySessionState,
  type LiveDocumentsClient,
  type LiveIssuesClient,
  type LiveResourceClient,
} from "./identityClient";

// Loaded on demand (not `preact/compat`'s lazy()/Suspense — importing preact/compat globally
// patches Preact's <select>/event handling and broke an unrelated existing test, "does not
// reuse one project's records for another project", even though that test never touches this
// panel) so Three.js and the vendored ifc-mesh-wasm engine (src/vendor/ifc-mesh-wasm/NOTICE.md)
// never load until a user actually opens the "Import real IFC" tab — the fixture-only Federation
// view (and every other page) must not pay for this chunk on first paint. A plain dynamic
// import(), triggered from a useEffect and tracked in local state, gets the same code-splitting
// (vite.config.ts's manualChunks still isolates it into one "ifc-viewer" chunk) without pulling
// in the compat layer.
type IfcViewerModules = {
  IfcModelViewport: typeof import("./components/IfcModelViewport")["IfcModelViewport"];
  IfcImportControls: typeof import("./components/IfcImportControls")["IfcImportControls"];
};

let ifcViewerModulesPromise: Promise<IfcViewerModules> | null = null;

function loadIfcViewerModules(): Promise<IfcViewerModules> {
  if (ifcViewerModulesPromise === null) {
    ifcViewerModulesPromise = Promise.all([
      import("./components/IfcModelViewport"),
      import("./components/IfcImportControls"),
    ])
      .then(([viewport, controls]) => ({
        IfcModelViewport: viewport.IfcModelViewport,
        IfcImportControls: controls.IfcImportControls,
      }))
      .catch((cause: unknown) => {
        ifcViewerModulesPromise = null;
        throw cause;
      });
  }
  return ifcViewerModulesPromise;
}

// A selected real-IFC element handed from the Federation "Import real IFC" panel to the Issues
// page's create-issue form (see App()'s pendingIssueSeed state). Deliberately just the display
// fields ImportedIfcPanel already has -- no model write, no BIM-Hub reference is created.
interface IssueElementSeed {
  readonly elementId: string;
  readonly elementLabel: string;
  readonly ifcClass: string | null;
  readonly storey: string;
}

const navigation: readonly { id: Page; label: string; glyph: string }[] = [
  { id: "project", label: "Project", glyph: "P" },
  { id: "federation", label: "Federation", glyph: "F" },
  { id: "documents", label: "Documents", glyph: "D" },
  { id: "issues", label: "Issues", glyph: "I" },
  { id: "rfis", label: "RFIs", glyph: "R" },
  { id: "approvals", label: "Approvals", glyph: "A" },
] as const;

const stateLabels: Record<ReferenceState, string> = {
  resolved: "Resolved",
  stale: "Stale",
  missing: "Missing",
  unsupported: "Unsupported",
  refused: "Refused",
};

function formatDate(value: string, includeTime = false): string {
  const date = new Date(value);
  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "short",
    ...(includeTime ? { hour: "2-digit", minute: "2-digit" } : {}),
  }).format(date);
}

function Badge({ children, tone }: { children: ComponentChildren; tone: string }) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

function stateSummary(references: readonly { referenceState: ReferenceState }[]): string {
  const counts = countReferenceStates(references);
  return (Object.entries(counts) as [ReferenceState, number][])
    .filter(([, count]) => count > 0)
    .map(([state, count]) => `${count} ${state}`)
    .join(" · ");
}

function CompletenessBanner({
  scope,
  completeness,
  children,
}: {
  scope: string;
  completeness: Completeness;
  children: ComponentChildren;
}) {
  if (completeness === "complete") return null;
  return (
    <div className="boundary-banner" role="status" data-testid={`${scope}-partial-banner`}>
      <span className="boundary-icon" aria-hidden="true">!</span>
      <div>
        <strong>Partial {scope}</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

function PageHeader({ eyebrow, title, summary, action }: {
  eyebrow: string;
  title: string;
  summary: string;
  action?: ComponentChildren;
}) {
  return (
    <header className="page-header">
      <div>
        <span className="eyebrow">{eyebrow}</span>
        <h1>{title}</h1>
        <p>{summary}</p>
      </div>
      {action}
    </header>
  );
}

function ProjectPage({ onNavigate, fixture }: { onNavigate: (page: Page) => void; fixture: Readonly<CollaborationFixture> }) {
  const openIssues = fixture.issues.filter((issue) => issue.workflowState !== "closed").length;
  const pendingApprovals = fixture.approvals.filter(
    (approval) => approval.status === "awaiting-review",
  ).length;
  const remainingReviews = fixture.approvals
    .filter((approval) => approval.status === "awaiting-review")
    .reduce((sum, approval) => sum + approval.reviewersTotal - approval.reviewersComplete, 0);
  const criticalIssues = fixture.issues.filter(
    (issue) => issue.priority === "critical" && issue.workflowState !== "closed",
  ).length;
  const federationCounts = countReferenceStates(fixture.federation.references);
  const projectCompleteness: Completeness = fixture.federation.completeness === "partial"
    || fixture.documentIndexCompleteness === "partial"
    || fixture.issueSyncCompleteness === "partial" ? "partial" : "complete";
  const priorityIssue = fixture.issues.find(
    (issue) => issue.priority === "critical" && issue.workflowState !== "closed",
  );

  return (
    <>
      <PageHeader
        eyebrow="Project command centre"
        title="Good morning, coordination team."
        summary="A precise view of the information that needs attention today."
        action={<button className="secondary-button" type="button" disabled>Export fixture report</button>}
      />
      <CompletenessBanner scope="project view" completeness={projectCompleteness}>
        Federation, document index, and issue sync exceptions remain visible below. No completion claim is made.
      </CompletenessBanner>

      <section className="metric-grid" aria-label="Project overview">
        <button className="metric-card" type="button" onClick={() => onNavigate("federation")}>
          <span>Federated references</span><strong>{fixture.federation.references.length}</strong>
          <small>{federationCounts.resolved} resolved · {fixture.federation.references.length - federationCounts.resolved} exceptions</small>
        </button>
        <button className="metric-card" type="button" onClick={() => onNavigate("documents")}>
          <span>Document versions</span><strong>{fixture.documents.length}</strong>
          <small>Index is {fixture.documentIndexCompleteness}</small>
        </button>
        <button className="metric-card" type="button" onClick={() => onNavigate("issues")}>
          <span>Open issues</span><strong>{openIssues}</strong>
          <small>{criticalIssues} critical coordination {criticalIssues === 1 ? "item" : "items"}</small>
        </button>
        <button className="metric-card" type="button" onClick={() => onNavigate("approvals")}>
          <span>Awaiting review</span><strong>{pendingApprovals}</strong>
          <small>{remainingReviews} reviewers remaining</small>
        </button>
      </section>

      <div className="dashboard-grid">
        <section className="panel focus-panel">
          <div className="section-heading">
            <div><span className="eyebrow">Priority</span><h2>Coordination focus</h2></div>
            <button className="text-button" type="button" onClick={() => onNavigate("issues")}>View issues →</button>
          </div>
          {priorityIssue ? <div className="priority-issue">
            <div className="issue-marker" aria-hidden="true">01</div>
            <div>
              <div className="meta-row"><Badge tone="critical">Critical</Badge><span>{priorityIssue.externalIdentifier}</span><span>Due {formatDate(priorityIssue.dueDate)}</span></div>
              <h3>{priorityIssue.title}</h3>
              <p>Assigned to {priorityIssue.assignee} · linked to coordination federation {fixture.federation.revisionLabel}</p>
            </div>
          </div> : <div className="inline-empty">No open critical coordination issues.</div>}
          <div className="reference-strip" aria-label="Federation exception summary">
            {(["stale", "missing", "unsupported", "refused"] as const).map((state) => (
              <div key={state}><span className={`status-dot dot-${state}`} />{stateLabels[state]}<strong>{federationCounts[state]}</strong></div>
            ))}
          </div>
        </section>

        <section className="panel activity-panel">
          <div className="section-heading"><div><span className="eyebrow">Latest</span><h2>Project activity</h2></div></div>
          <ol className="activity-list">
            {fixture.activity.map((activity) => (
              <li key={activity.id}>
                <span className="activity-pip" aria-hidden="true" />
                <div><strong>{activity.actor}</strong> {activity.verb}<br /><span>{activity.subject}</span></div>
                <time dateTime={activity.occurredAt}>{formatDate(activity.occurredAt, true)}</time>
              </li>
            ))}
            {fixture.activity.length === 0 && <li className="inline-empty">No fixture activity.</li>}
          </ol>
        </section>
      </div>

      <section className="panel schedule-boundary-panel" aria-label="Controls schedule boundary">
        <div className="section-heading"><div><span className="eyebrow">Fixture-only boundary</span><h2>Controls schedule relationship</h2></div><button className="secondary-button" type="button" disabled>Open Controls deep link</button></div>
        <p>CDE workflow due dates remain independent. A Controls relationship is shown only when this fixture declares one explicitly.</p>
        <div className="schedule-scenario-list">
          {fixture.scheduleBoundaryScenarios.map((scenario) => (
            <article className="schedule-scenario" key={scenario.id}>
              <div><span className="scenario-kind">{scenario.cdeRecordKind}</span><h3>{scenario.cdeRecordIdentifier}</h3><small>CDE due {formatDate(scenario.cdeDueDate)}</small></div>
              <Badge tone={scenario.relationship}>{scenario.relationship === "unlinked" ? "No linked task" : "Explicit candidate"}</Badge>
              <div className="scenario-controls-reference">{scenario.candidateControlsReference ? <><strong>{String(scenario.candidateControlsReference.opaqueIdentifier)}</strong><span>{String(scenario.candidateControlsReference.scheme)}</span></> : <span>No Controls reference supplied</span>}</div>
              <p>{scenario.annotation}</p>
            </article>
          ))}
          {fixture.scheduleBoundaryScenarios.length === 0 && <div className="empty-state">No schedule-boundary fixture scenarios.</div>}
        </div>
      </section>
    </>
  );
}

function ModelStage({
  selected,
  elements,
  selectedElementId,
  onSelectElement,
}: {
  selected: ModelReference;
  elements: readonly ElementReference[];
  selectedElementId: string;
  onSelectElement: (elementId: string) => void;
}) {
  return (
    <div className="model-stage" aria-label="Reference-only federation preview">
      <div className="stage-toolbar">
        <span>Fixture element map</span>
        <span className="stage-tag">No geometry loaded</span>
      </div>
      <svg viewBox="0 0 720 440" role="img" aria-label="Abstract building massing placeholder">
        <defs>
          <linearGradient id="wall" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#d7d8cf" /><stop offset="1" stopColor="#9ea59b" />
          </linearGradient>
          <linearGradient id="glass" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#9eb3b3" stopOpacity=".78" /><stop offset="1" stopColor="#596f70" stopOpacity=".9" />
          </linearGradient>
        </defs>
        <path d="M80 360 362 222 659 350 367 426Z" fill="#d9dad3" />
        <path d="M171 307 354 220 526 292 348 384Z" fill="url(#wall)" stroke="#fff" />
        <path d="M171 307 348 384 348 243 171 177Z" fill="#b9beb6" stroke="#fff" />
        <path d="M348 384 526 292 526 164 348 243Z" fill="url(#glass)" stroke="#fff" />
        <path d="M171 177 348 243 526 164 349 105Z" fill="#ebe9df" stroke="#fff" />
        <path d="M215 194V326M260 211V346M305 227V365M394 222V361M440 202V338M483 183V316" stroke="#edf1eb" strokeWidth="3" opacity=".8" />
        <path d="m102 339 37-18 32 14-37 19Z" fill="#c7c9c0" /><path d="m544 329 35-17 37 15-36 18Z" fill="#c7c9c0" />
        <path d="M90 387H644" stroke="#b4b8af" strokeDasharray="4 6" />
        <circle cx="348" cy="243" r="8" fill="#d75a40" stroke="#fff" strokeWidth="3" />
      </svg>
      <div className="fixture-element-pins" aria-label="Fixture element selection">
        {elements.map((element, index) => (
          <button
            type="button"
            key={element.id}
            className={`${element.id === selectedElementId ? "element-pin selected" : "element-pin"} pin-${index}`}
            onClick={() => onSelectElement(element.id)}
            aria-pressed={element.id === selectedElementId}
          >
            <span className={`status-dot dot-${element.referenceState}`} aria-hidden="true" />
            <span>Select {element.label}</span>
          </button>
        ))}
      </div>
      <div className="stage-caption">
        <span className={`status-dot dot-${selected.referenceState}`} />
        <strong>{selected.name}</strong><span>{selected.externalIdentifier}</span>
      </div>
    </div>
  );
}

// Real, user-selected IFC geometry, decoded and rendered entirely client-side (see
// src/domain/ifcModelImport.ts and src/vendor/ifc-mesh-wasm/NOTICE.md). Deliberately a separate
// panel from the fixture-only federation manifest above: this shows an opaque mesh for a file
// the browser just parsed, not a BIM-Hub federation reference, and the two must not be visually
// confused with each other.
function ImportedIfcPanel({
  session,
  onCreateIssueFromElement,
}: {
  session: IdentitySessionState;
  onCreateIssueFromElement: (seed: IssueElementSeed) => void;
}) {
  const [modules, setModules] = useState<IfcViewerModules | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [importedModel, setImportedModel] = useState<ImportedIfcModel | null>(null);
  const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
  const selectedElement = importedModel?.elements.find((element) => element.id === selectedElementId) ?? null;

  useEffect(() => {
    let cancelled = false;
    loadIfcViewerModules()
      .then((loaded) => {
        if (!cancelled) setModules(loaded);
      })
      .catch((cause: unknown) => {
        if (!cancelled) setLoadError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="federation-layout ifc-real-layout">
      <div className="model-stage ifc-real-stage" aria-label="Real IFC 3D viewport">
        <div className="stage-toolbar">
          <span>{importedModel ? importedModel.sourceFileName : "No file imported yet"}</span>
          {importedModel && <span className="stage-tag">{importedModel.triangleCount.toLocaleString("en-GB")} triangles · {importedModel.chunkCount} chunks</span>}
        </div>
        <ErrorBoundary label="Real IFC viewer">
          {loadError ? (
            <div className="model-fallback error-boundary" role="alert">
              <strong>Real IFC viewer failed to load</strong>
              <p>{loadError}</p>
            </div>
          ) : !modules ? (
            <div className="model-loading" role="status"><span />Loading IFC engine…</div>
          ) : importedModel ? (
            <modules.IfcModelViewport
              chunks={importedModel.chunks}
              elementIdByInstance={importedModel.elementIdByInstance}
              selectedElementId={selectedElementId}
              onSelectElement={setSelectedElementId}
            />
          ) : (
            <modules.IfcImportControls
              onImported={(model) => {
                setImportedModel(model);
                setSelectedElementId(null);
              }}
            />
          )}
        </ErrorBoundary>
        {importedModel && (
          <button
            type="button"
            className="secondary-button ifc-change-model"
            onClick={() => {
              setImportedModel(null);
              setSelectedElementId(null);
            }}
          >
            Change model
          </button>
        )}
      </div>
      <aside className="reference-panel">
        <div className="section-heading"><div><span className="eyebrow">Client-side import</span><h2>Real IFC model</h2></div></div>
        <p className="ifc-real-boundary-note">
          Parsed and rendered entirely in this browser via a vendored, display-only WASM engine. Nothing is uploaded, and this
          panel does not become a second copy of BIM-Hub model authority — it is opaque geometry for the file you chose.
        </p>
        {importedModel && (
          <dl className="reference-detail">
            <div><dt>Elements</dt><dd>{importedModel.elements.length}</dd></div>
            <div><dt>Storeys</dt><dd>{importedModel.storeys.length}</dd></div>
            <div><dt>Source size</dt><dd>{(importedModel.sourceBytes / 1024).toLocaleString("en-GB", { maximumFractionDigits: 1 })} KB</dd></div>
            <div><dt>SHA-256</dt><dd className="mono-truncate">{importedModel.sourceSha256.slice(0, 16)}…</dd></div>
          </dl>
        )}
        {selectedElement && (
          <section className="element-detail" aria-label="Selected imported element">
            <span className="eyebrow">Selected element</span>
            <h3>{selectedElement.name}</h3>
            <dl>
              <div><dt>GUID</dt><dd>{selectedElement.id}</dd></div>
              <div><dt>IFC class</dt><dd>{selectedElement.ifcClass ?? "Unsupported / not reported"}</dd></div>
              <div><dt>Storey</dt><dd>{selectedElement.level}</dd></div>
            </dl>
            {session.status === "signed-in" ? (
              <button
                className="secondary-button"
                type="button"
                onClick={() => onCreateIssueFromElement({
                  elementId: selectedElement.id,
                  elementLabel: selectedElement.name,
                  ifcClass: selectedElement.ifcClass,
                  storey: selectedElement.level,
                })}
              >
                Create issue from this element
              </button>
            ) : (
              <button className="secondary-button" type="button" disabled>Sign in to create an issue</button>
            )}
            <p>Selection is local to this browser session. No model data is written; a created issue references this element's GUID only.</p>
          </section>
        )}
      </aside>
    </div>
  );
}

function FederationPage({
  fixture,
  session,
  onCreateIssueFromElement,
}: {
  fixture: Readonly<CollaborationFixture>;
  session: IdentitySessionState;
  onCreateIssueFromElement: (seed: IssueElementSeed) => void;
}) {
  const references = fixture.federation.references;
  const elements = fixture.federation.elements;
  const [selectedId, setSelectedId] = useState(references[0]?.id ?? "");
  const [selectedElementId, setSelectedElementId] = useState(elements[0]?.id ?? "");
  const [view, setView] = useState<"fixture" | "import">("fixture");
  const selected = references.find((reference) => reference.id === selectedId) ?? references[0];
  const selectedElement = elements.find((element) => element.id === selectedElementId) ?? elements[0];
  const counts = countReferenceStates(references);

  const viewTabs = (
    <div className="federation-view-tabs" role="tablist" aria-label="Federation view">
      <button type="button" role="tab" aria-selected={view === "fixture"} onClick={() => setView("fixture")}>Fixture manifest</button>
      <button type="button" role="tab" aria-selected={view === "import"} onClick={() => setView("import")}>Import real IFC</button>
    </div>
  );

  if (!selected) return (
    <>
      <PageHeader eyebrow="Federation / EMPTY" title="No model references" summary="This fixture defines a known empty federation manifest." action={<Badge tone="complete">Complete empty set</Badge>} />
      {viewTabs}
      {view === "fixture" ? (
        <div className="empty-state panel">No model revision references are present in this fixture.</div>
      ) : (
        <ImportedIfcPanel session={session} onCreateIssueFromElement={onCreateIssueFromElement} />
      )}
    </>
  );

  return (
    <>
      <PageHeader
        eyebrow={`Federation / ${fixture.federation.revisionLabel}`}
        title={fixture.federation.name}
        summary="Immutable model revision references, accounted for without loading source content."
        action={<Badge tone="partial">Partial federation</Badge>}
      />
      {viewTabs}
      {view === "import" ? (
        <ImportedIfcPanel session={session} onCreateIssueFromElement={onCreateIssueFromElement} />
      ) : (
        <>
          <CompletenessBanner scope="federation" completeness={fixture.federation.completeness}>
            Only {counts.resolved} of {references.length} references resolved. Every exception remains classified below.
          </CompletenessBanner>
          <div className="federation-layout">
            <ModelStage selected={selected} elements={elements} selectedElementId={selectedElementId} onSelectElement={setSelectedElementId} />
            <aside className="reference-panel">
              <div className="section-heading"><div><span className="eyebrow">Manifest</span><h2>Model references</h2></div><span>{references.length}</span></div>
              <div className="reference-list">
                {references.map((reference) => (
                  <button
                    type="button"
                    key={reference.id}
                    className={reference.id === selectedId ? "reference-row selected" : "reference-row"}
                    onClick={() => setSelectedId(reference.id)}
                  >
                    <span className={`discipline-mark mark-${reference.referenceState}`}>{reference.discipline}</span>
                    <span><strong>{reference.name}</strong><small>{reference.revision} · {reference.source}</small></span>
                    <Badge tone={reference.referenceState}>{stateLabels[reference.referenceState]}</Badge>
                  </button>
                ))}
              </div>
              <dl className="reference-detail">
                <div><dt>External ID</dt><dd>{selected.externalIdentifier}</dd></div>
                <div><dt>Received</dt><dd>{formatDate(selected.receivedAt, true)}</dd></div>
                <div><dt>Retained fields</dt><dd>{retainedFieldCount(selected)} verbatim</dd></div>
              </dl>
              {selectedElement && <section className="element-detail" aria-label="Selected fixture element">
                <span className="eyebrow">Selected fixture element</span>
                <h3>{selectedElement.label}</h3>
                <dl>
                  <div><dt>Element ID</dt><dd>{selectedElement.elementIdentifier}</dd></div>
                  <div><dt>Revision</dt><dd>{selectedElement.modelRevisionIdentifier}</dd></div>
                  <div><dt>Locator</dt><dd>{String(selectedElement.locator.scheme)}</dd></div>
                  <div><dt>State</dt><dd><Badge tone={selectedElement.referenceState}>{stateLabels[selectedElement.referenceState]}</Badge></dd></div>
                </dl>
                <button className="secondary-button" type="button" disabled>Attach to issue draft (C2)</button>
                <p>Selection is fixture-only. No issue, viewpoint, or model data is written.</p>
              </section>}
            </aside>
          </div>
        </>
      )}
    </>
  );
}

// Written through this app's own D1-backed shadow API's document_versions upload/download
// endpoints (functions/api/shadow/document_versions/{upload,download}.ts; see
// functions/lib/shadowDocumentHandlers.ts and src/c1/shadowFoundation/documentLoop.ts for the
// digest/cap logic this UI never reimplements). Deliberately not built on this file's generic
// LiveResourcePanel: upload here needs a raw byte body (a picked file, not a JSON create() body)
// and download needs to turn a byte response into a browser file save, neither of which
// LiveResourcePanel's contract expresses -- LiveIssuesPanel above took the same "new sibling
// rather than force-fit the generic panel" call for the same reason. List is unauthenticated
// (GET /api/shadow/document_versions carries no ACL check, same as every other shadow resource)
// so it loads for signed-out visitors too; only upload requires a signed-in session.
function LiveDocumentsPanel({ session, client }: { session: IdentitySessionState; client: LiveDocumentsClient }) {
  const [rows, setRows] = useState<readonly DocumentVersionRow[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [documentId, setDocumentId] = useState("");
  const [version, setVersion] = useState("1");
  const [title, setTitle] = useState("");
  const [kind, setKind] = useState("drawing");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [lastUpload, setLastUpload] = useState<Pick<DocumentVersionRow, "documentId" | "version" | "contentSha256" | "byteCount"> | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const reload = () => {
    client
      .list()
      .then((loaded) => {
        setRows(loaded);
        setListError(null);
      })
      .catch((cause: unknown) => setListError(cause instanceof Error ? cause.message : String(cause)));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [client]);

  const parsedVersion = Number(version);
  const missingRequired =
    documentId.trim() === "" || title.trim() === "" || file === null || !Number.isInteger(parsedVersion) || parsedVersion <= 0;

  async function submitUpload(): Promise<void> {
    if (session.status !== "signed-in" || file === null || missingRequired) return;
    setUploading(true);
    setUploadError(null);
    const bytes = new Uint8Array(await file.arrayBuffer());
    const result = await client.upload({
      documentId: documentId.trim(),
      version: parsedVersion,
      title: title.trim(),
      kind: kind.trim() || "document",
      bytes,
    });
    setUploading(false);
    if (!result.ok || !result.row) {
      setUploadError(result.reason ?? "Upload failed.");
      return;
    }
    setLastUpload(result.row);
    setDocumentId("");
    setVersion("1");
    setTitle("");
    setFile(null);
    setFormOpen(false);
    reload();
  }

  // Triggers a real browser file save from the downloaded (and digest-reverified server-side, see
  // functions/lib/shadowDocumentHandlers.ts) bytes -- an anchor click is the ordinary way to do
  // this from an already-fetched Blob without navigating the page away.
  async function triggerDownload(row: DocumentVersionRow): Promise<void> {
    setDownloadError(null);
    const result = await client.download(row.documentId, row.version);
    if (!result.ok || !result.bytes) {
      setDownloadError(result.reason ?? "Download failed.");
      return;
    }
    // Cast needed under TS 7's stricter Uint8Array<ArrayBufferLike> vs BlobPart typing -- the
    // bytes are ordinary ArrayBuffer-backed data from a fetch response, never a SharedArrayBuffer.
    const blob = new Blob([result.bytes as BlobPart], { type: "application/octet-stream" });
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = objectUrl;
    anchor.download = `${row.documentId}-v${row.version}-${row.title}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }

  return (
    <section className="panel live-resource-panel" aria-label="Live documents">
      <div className="section-heading">
        <div><span className="eyebrow">Identity sign-in</span><h2>Live documents</h2></div>
        {session.status === "signed-in" ? (
          <button className="primary-button" type="button" onClick={() => setFormOpen((open) => !open)}>
            {formOpen ? "Cancel" : "Upload live document"}
          </button>
        ) : (
          <button className="primary-button" type="button" disabled>Sign in to upload a document</button>
        )}
      </div>
      <p>
        Saved through this app's live shadow API (an R2 object plus a D1 row), not the fixture register below.
        Visible to anyone here (including after a reload); only a signed-in uploader can add a version.
      </p>
      {session.status === "signed-in" && formOpen && (
        <form
          className="live-resource-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitUpload();
          }}
        >
          <label>Document ID
            <input required value={documentId} onChange={(event) => setDocumentId(event.currentTarget.value)} aria-label="New document ID" />
          </label>
          <label>Version
            <input required type="number" min="1" step="1" value={version} onChange={(event) => setVersion(event.currentTarget.value)} aria-label="New document version" />
          </label>
          <label>Title
            <input required value={title} onChange={(event) => setTitle(event.currentTarget.value)} aria-label="New document title" />
          </label>
          <label>Kind
            <input value={kind} onChange={(event) => setKind(event.currentTarget.value)} aria-label="New document kind" />
          </label>
          <label>File
            {/* Not `required`: jsdom does not mark a file input's constraint validity satisfied
                by a fireEvent-assigned FileList (verified while writing this component's test),
                which would silently block form submission under `npm run test` even though a
                real browser's file picker satisfies it correctly. The `missingRequired`-gated
                submit button above already enforces "a file must be chosen" without relying on
                native constraint validation. */}
            <input type="file" onChange={(event) => setFile(event.currentTarget.files?.[0] ?? null)} aria-label="New document file" />
          </label>
          <button className="primary-button" type="submit" disabled={uploading || missingRequired}>
            {uploading ? "Uploading…" : "Save document version"}
          </button>
          {uploadError && <p role="alert" className="form-error">{uploadError}</p>}
        </form>
      )}
      {lastUpload && (
        <div className="boundary-banner" role="status" data-testid="document-upload-result">
          <span className="boundary-icon" aria-hidden="true">i</span>
          <div>
            <strong>Uploaded {lastUpload.documentId} v{lastUpload.version}</strong>
            <p>{lastUpload.byteCount.toLocaleString("en-GB")} bytes · sha256 {lastUpload.contentSha256}</p>
          </div>
        </div>
      )}
      {listError && <p role="alert" className="form-error">Live documents could not be loaded: {listError}</p>}
      {downloadError && <p role="alert" className="form-error">{downloadError}</p>}
      <ul className="live-resource-list">
        {rows.map((row) => (
          <li key={row.id} data-testid="live-document-row">
            <strong>{row.title}</strong>
            <span>{row.documentId} · v{row.version} · {row.kind}</span>
            <span>{row.byteCount.toLocaleString("en-GB")} bytes · sha256 {row.contentSha256}</span>
            <button className="primary-button" type="button" onClick={() => void triggerDownload(row)}>Download</button>
          </li>
        ))}
        {rows.length === 0 && !listError && <li className="inline-empty">No live document versions yet.</li>}
      </ul>
    </section>
  );
}

function DocumentsPage({
  fixture,
  session,
  liveDocumentsClient,
}: {
  fixture: Readonly<CollaborationFixture>;
  session: IdentitySessionState;
  liveDocumentsClient: LiveDocumentsClient;
}) {
  const documents = sortDocuments(fixture.documents);
  const renderWindow = getRenderWindow(documents);
  const partialDocuments = documents.filter((document) => document.indexCompleteness === "partial").length;
  return (
    <>
      <PageHeader eyebrow="Controlled information" title="Documents" summary="Version-specific records; source content is not requested by this fixture shell." action={<button className="primary-button" type="button" disabled>Upload document</button>} />
      <LiveDocumentsPanel session={session} client={liveDocumentsClient} />
      <CompletenessBanner scope="document index" completeness={fixture.documentIndexCompleteness}>
        {partialDocuments.toLocaleString("en-GB")} records are not fully indexed. Search results must not be treated as exhaustive.
      </CompletenessBanner>
      {renderWindow.requiresVirtualization && <div className="boundary-banner" role="status" data-testid="document-render-window">
        <span className="boundary-icon" aria-hidden="true">!</span><div><strong>Bounded document rendering</strong><p>Showing {renderWindow.records.length.toLocaleString("en-GB")} of {documents.length.toLocaleString("en-GB")} records; {renderWindow.omitted.toLocaleString("en-GB")} remain accounted for.</p></div>
      </div>}
      <section className="panel table-panel">
        <div className="table-tools"><label className="search-field"><span aria-hidden="true">⌕</span><input type="search" placeholder="Search documents" aria-label="Search documents" /></label><span className="fixture-note">{documents.length} fixture records</span></div>
        <div className="table-scroll" tabIndex={0} role="region" aria-label="Document table, scrollable"><table>
          <thead><tr><th>Document</th><th>Type</th><th>Revision</th><th>Owner</th><th>Modified</th><th>Reference</th></tr></thead>
          <tbody>{renderWindow.records.map((document) => (
            <tr key={document.id} data-testid="document-row">
              <td><strong>{document.title}</strong><small>{document.documentNumber}</small></td>
              <td>{document.kind}</td><td>{document.revision}</td><td>{document.owner}</td><td>{formatDate(document.modifiedAt)}</td>
              <td><Badge tone={document.referenceState}>{stateLabels[document.referenceState]}</Badge>{retainedFieldCount(document) > 0 && <small>{retainedFieldCount(document)} retained</small>}</td>
            </tr>
          ))}{documents.length === 0 && <tr><td colSpan={6}><div className="empty-state">No document versions in this fixture.</div></td></tr>}</tbody>
        </table></div>
      </section>
    </>
  );
}

// Written through this app's own D1-backed shadow API (functions/api/shadow/issues, see
// functions/lib/shadowCrudHandlers.ts), not the fixture manifest below -- deliberately a
// separate, distinctly-labelled section, the same "don't visually confuse an opaque real thing
// with fixture data" discipline ImportedIfcPanel already follows for real IFC geometry. Listing
// is unauthenticated (GET /api/shadow/issues carries no ACL check; see that file's header
// comment), so every visitor sees what has actually been created here, including after a
// reload -- only *creating* a row requires a signed-in session.
function LiveIssuesPanel({
  session,
  liveIssuesClient,
  seed,
  onConsumeSeed,
}: {
  session: IdentitySessionState;
  liveIssuesClient: LiveIssuesClient;
  seed: IssueElementSeed | null;
  onConsumeSeed: () => void;
}) {
  const [liveIssues, setLiveIssues] = useState<readonly ShadowIssueRow[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [topicType, setTopicType] = useState("comment");
  const [priority, setPriority] = useState<IssuePriority>("normal");
  const [assigneeSubject, setAssigneeSubject] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Load a pre-submit draft from browser-local storage on mount. This is intentionally separate
  // from the submitted-issue list (which lives in the server-side shadow API), satisfying the
  // orchestrator's condition that drafts and submitted issues must not share a key.
  useEffect(() => {
    const draft = loadIssueDraft();
    if (draft) {
      setTitle(draft.title);
      setTopicType(draft.topicType);
      setPriority(draft.priority);
      setAssigneeSubject(draft.assigneeSubject);
      setDueDate(draft.dueDate);
    }
  }, []);

  // Persist draft fields whenever they change. Empty drafts are also persisted (as empty strings),
  // which lets a user clear the form and have it stay cleared across reloads.
  useEffect(() => {
    saveIssueDraft({ title, topicType, priority, assigneeSubject, dueDate });
  }, [title, topicType, priority, assigneeSubject, dueDate]);

  const reloadLiveIssues = () => {
    liveIssuesClient
      .list()
      .then((rows) => {
        setLiveIssues(rows);
        setListError(null);
      })
      .catch((cause: unknown) => setListError(cause instanceof Error ? cause.message : String(cause)));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reloadLiveIssues, [liveIssuesClient]);

  useEffect(() => {
    if (seed === null) return;
    setFormOpen(true);
    setTitle((current) => (current.trim() === "" ? `Issue on ${seed.elementLabel}` : current));
    setAssigneeSubject((current) => (current.trim() === "" && session.subject ? session.subject : current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed]);

  async function submitIssue(): Promise<void> {
    if (session.status !== "signed-in") return;
    setSubmitting(true);
    setSubmitError(null);
    const id = `issue:live:${crypto.randomUUID()}`;
    const nowIso = new Date().toISOString();
    const body: Record<string, unknown> = {
      id,
      externalIdentifier: id,
      title: title.trim(),
      topicType: topicType.trim() || "comment",
      priority,
      assigneeSubject: assigneeSubject.trim() || session.subject || "unassigned",
      dueDate: dueDate.trim() || nowIso.slice(0, 10),
      workflowState: "open",
      createdAt: nowIso,
      ...(seed
        ? {
            sourceElementId: seed.elementId,
            sourceElementLabel: seed.elementLabel,
            sourceElementIfcClass: seed.ifcClass,
            sourceElementStorey: seed.storey,
          }
        : {}),
    };
    const result = await liveIssuesClient.create(body);
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.reason ?? "Create failed.");
      return;
    }
    setTitle("");
    setTopicType("comment");
    setPriority("normal");
    setAssigneeSubject("");
    setDueDate("");
    setFormOpen(false);
    saveIssueDraft(null);
    onConsumeSeed();
    reloadLiveIssues();
  }

  return (
    <section className="panel live-issues-panel" aria-label="Live issues">
      <div className="section-heading">
        <div><span className="eyebrow">Identity sign-in</span><h2>Live issues</h2></div>
        {session.status === "signed-in" ? (
          <button className="primary-button" type="button" onClick={() => setFormOpen((open) => !open)}>
            {formOpen ? "Cancel" : "Create issue"}
          </button>
        ) : (
          <button className="primary-button" type="button" disabled>Sign in to create an issue</button>
        )}
      </div>
      <p>
        Saved through this app's live shadow API, not the fixture register below. Visible to anyone here (including after a
        reload); only a signed-in creator can add one, and only that creator can delete or restore it afterward.
      </p>
      {seed && (
        <div className="boundary-banner" role="status">
          <span className="boundary-icon" aria-hidden="true">i</span>
          <div><strong>Linked to a selected IFC element</strong><p>{seed.elementLabel} ({seed.ifcClass ?? "unsupported class"}), storey {seed.storey}.</p></div>
        </div>
      )}
      {session.status === "signed-in" && formOpen && (
        <form
          className="live-issue-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submitIssue();
          }}
        >
          <label>Title
            <input required value={title} onChange={(event) => setTitle(event.currentTarget.value)} aria-label="New issue title" />
          </label>
          <label>Topic type
            <input value={topicType} onChange={(event) => setTopicType(event.currentTarget.value)} aria-label="New issue topic type" />
          </label>
          <label>Priority
            <select value={priority} onChange={(event) => setPriority(event.currentTarget.value as IssuePriority)} aria-label="New issue priority">
              {issuePriorities.map((option) => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <label>Assignee subject
            <input value={assigneeSubject} onChange={(event) => setAssigneeSubject(event.currentTarget.value)} aria-label="New issue assignee subject" />
          </label>
          <label>Due date
            <input type="date" value={dueDate} onChange={(event) => setDueDate(event.currentTarget.value)} aria-label="New issue due date" />
          </label>
          <button className="primary-button" type="submit" disabled={submitting || title.trim() === ""}>
            {submitting ? "Creating…" : "Save issue"}
          </button>
          {submitError && <p role="alert" className="form-error">{submitError}</p>}
        </form>
      )}
      {listError && <p role="alert" className="form-error">Live issues could not be loaded: {listError}</p>}
      <ul className="live-issue-list">
        {liveIssues.map((issue) => (
          <li key={issue.id} data-testid="live-issue-row">
            <strong>{issue.title}</strong>
            <span>{issue.topicType} · {issue.priority} priority · {issue.workflowState}</span>
            <span>Assigned to {issue.assigneeSubject} · due {issue.dueDate}</span>
            {typeof issue.unknownFields?.sourceElementLabel === "string" && (
              <span>Linked element: {issue.unknownFields.sourceElementLabel}</span>
            )}
          </li>
        ))}
        {liveIssues.length === 0 && !listError && <li className="inline-empty">No live issues yet.</li>}
      </ul>
    </section>
  );
}

function IssuesPage({
  fixture,
  session,
  liveIssuesClient,
  seed,
  onConsumeSeed,
}: {
  fixture: Readonly<CollaborationFixture>;
  session: IdentitySessionState;
  liveIssuesClient: LiveIssuesClient;
  seed: IssueElementSeed | null;
  onConsumeSeed: () => void;
}) {
  const [query, setQuery] = useState("");
  const [workflowState, setWorkflowState] = useState<IssueRecord["workflowState"] | "all">("all");
  const [expandedIssueId, setExpandedIssueId] = useState<string | null>(null);
  const issues = useMemo(() => filterIssues(fixture.issues, query, workflowState), [fixture, query, workflowState]);
  const renderWindow = getRenderWindow(issues);
  const partialIssues = fixture.issues.filter((issue) => issue.syncCompleteness === "partial").length;
  return (
    <>
      <PageHeader eyebrow="Coordination register" title="Issues" summary="BCF-oriented fixture topics. This shell is not an authoritative BCF server." action={<button className="primary-button" type="button" disabled>New fixture issue</button>} />
      <LiveIssuesPanel session={session} liveIssuesClient={liveIssuesClient} seed={seed} onConsumeSeed={onConsumeSeed} />
      <CompletenessBanner scope="issue sync" completeness={fixture.issueSyncCompleteness}>
        {partialIssues.toLocaleString("en-GB")} imported topics have partial extension data. Unknown values remain retained and unmodified.
      </CompletenessBanner>
      {renderWindow.requiresVirtualization && <div className="boundary-banner" role="status" data-testid="issue-render-window">
        <span className="boundary-icon" aria-hidden="true">!</span><div><strong>Bounded issue rendering</strong><p>Showing {renderWindow.records.length.toLocaleString("en-GB")} of {issues.length.toLocaleString("en-GB")} records; {renderWindow.omitted.toLocaleString("en-GB")} remain accounted for.</p></div>
      </div>}
      <section className="panel table-panel">
        <div className="table-tools">
          <label className="search-field"><span aria-hidden="true">⌕</span><input value={query} onChange={(event) => setQuery(event.currentTarget.value)} type="search" placeholder="Search issues" aria-label="Search issues" /></label>
          <select value={workflowState} onChange={(event) => setWorkflowState(event.currentTarget.value as typeof workflowState)} aria-label="Filter issue status">
            <option value="all">All states</option><option value="open">Open</option><option value="in-review">In review</option><option value="closed">Closed</option>
          </select>
        </div>
        <div className="issue-list">
          {renderWindow.records.map((issue) => (
            <article className="issue-row" key={issue.id} data-testid="issue-row">
              <span className={`priority-line priority-${issue.priority}`} />
              <div className="issue-main"><span>{issue.externalIdentifier} · {issue.topicType} · {issue.priority} priority</span><h3>{issue.title}</h3><small>{issue.assignee} · due {formatDate(issue.dueDate)}</small><span className="issue-link-summary" data-testid={`issue-links-${issue.id}`}>{issue.elementReferences.length} element refs · {stateSummary(issue.elementReferences)}{issue.viewpoint ? ` · viewpoint ${issue.viewpoint.referenceState}` : ""}</span>{(issue.elementReferences.length > 0 || issue.viewpoint) && <button className="issue-reference-toggle" type="button" aria-expanded={expandedIssueId === issue.id} onClick={() => setExpandedIssueId((current) => current === issue.id ? null : issue.id)}>Inspect linked references</button>}</div>
              <Badge tone={issue.referenceState}>{stateLabels[issue.referenceState]}</Badge><Badge tone={issue.workflowState}>{issue.workflowState.replace("-", " ")}</Badge>
              <div className="retained-count">{retainedFieldCount(issue)}<small>retained</small></div>
              {expandedIssueId === issue.id && <section className="issue-reference-detail" aria-label={`${issue.externalIdentifier} linked references`} data-testid={`issue-reference-detail-${issue.id}`}>
                <h4>Linked element references</h4>
                <ul>{issue.elementReferences.map((reference) => <li key={reference.id}><Badge tone={reference.referenceState}>{stateLabels[reference.referenceState]}</Badge><div><strong>{reference.label}</strong><span>{reference.elementIdentifier}</span><span>{reference.modelRevisionIdentifier} · {String(reference.locator.scheme)}</span></div></li>)}</ul>
                {issue.viewpoint && <div className="viewpoint-reference"><Badge tone={issue.viewpoint.referenceState}>{stateLabels[issue.viewpoint.referenceState]}</Badge><span>Viewpoint · {String(issue.viewpoint.locator.scheme)}</span></div>}
                <p>Opaque fixture references only. No BCF package, model content, or issue update was requested.</p>
              </section>}
            </article>
          ))}
          {issues.length === 0 && <div className="empty-state">No fixture issues match this filter.</div>}
        </div>
      </section>
    </>
  );
}

// Shared field-driven create form + list for a `functions/api/shadow/{resource}` path segment
// (issues/rfis/approvals all share the same list()/create() wire contract, see
// src/identityClient.ts's LiveResourceClient). This is deliberately a *new*, generic sibling to
// LiveIssuesPanel rather than a refactor of it -- LiveIssuesPanel's DOM shape (button/label text,
// data-testid values) is already exercised by C1-IDENTITY-SIGNIN-AND-ISSUE-SAVE and by kaz's real
// browser confirmation of that exact loop; re-deriving it from this generic component risks
// silently changing that confirmed behaviour. RfisPage/ApprovalsPage below are new call sites, so
// they can safely share this one implementation instead of duplicating the panel three times.
interface LiveResourceFieldConfig {
  readonly name: string;
  readonly label: string;
  readonly kind: "text" | "select" | "date";
  readonly options?: readonly string[];
  readonly required?: boolean;
}

interface LiveResourceRow {
  readonly id: string;
  readonly unknownFields?: Readonly<Record<string, unknown>>;
}

function LiveResourcePanel<Row extends LiveResourceRow>({
  panelLabel,
  entityLabel,
  createButtonLabel,
  signedOutButtonLabel,
  idPrefix,
  session,
  client,
  fields,
  defaultValues,
  buildBody,
  renderRow,
  emptyLabel,
}: {
  panelLabel: string;
  entityLabel: string;
  createButtonLabel: string;
  signedOutButtonLabel: string;
  idPrefix: string;
  session: IdentitySessionState;
  client: LiveResourceClient<Row>;
  fields: readonly LiveResourceFieldConfig[];
  defaultValues: (session: IdentitySessionState) => Record<string, string>;
  buildBody: (values: Record<string, string>, session: IdentitySessionState, id: string, nowIso: string) => Record<string, unknown>;
  renderRow: (row: Row) => ComponentChildren;
  emptyLabel: string;
}) {
  const [rows, setRows] = useState<readonly Row[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [values, setValues] = useState<Record<string, string>>(() => defaultValues(session));
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const reload = () => {
    client
      .list()
      .then((loaded) => {
        setRows(loaded);
        setListError(null);
      })
      .catch((cause: unknown) => setListError(cause instanceof Error ? cause.message : String(cause)));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [client]);

  async function submit(): Promise<void> {
    if (session.status !== "signed-in") return;
    setSubmitting(true);
    setSubmitError(null);
    const id = `${idPrefix}:${crypto.randomUUID()}`;
    const nowIso = new Date().toISOString();
    const result = await client.create(buildBody(values, session, id, nowIso));
    setSubmitting(false);
    if (!result.ok) {
      setSubmitError(result.reason ?? "Create failed.");
      return;
    }
    setValues(defaultValues(session));
    setFormOpen(false);
    reload();
  }

  const missingRequired = fields.some((field) => field.required && (values[field.name] ?? "").trim() === "");

  return (
    <section className="panel live-resource-panel" aria-label={panelLabel}>
      <div className="section-heading">
        <div><span className="eyebrow">Identity sign-in</span><h2>{panelLabel}</h2></div>
        {session.status === "signed-in" ? (
          <button className="primary-button" type="button" onClick={() => setFormOpen((open) => !open)}>
            {formOpen ? "Cancel" : createButtonLabel}
          </button>
        ) : (
          <button className="primary-button" type="button" disabled>{signedOutButtonLabel}</button>
        )}
      </div>
      <p>
        Saved through this app's live shadow API, not the fixture list below. Visible to anyone here (including after a
        reload); only a signed-in creator can add {entityLabel}.
      </p>
      {session.status === "signed-in" && formOpen && (
        <form
          className="live-resource-form"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          {fields.map((field) => (
            <label key={field.name}>
              {field.label}
              {field.kind === "select" ? (
                <select
                  value={values[field.name] ?? ""}
                  onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.currentTarget.value }))}
                  aria-label={field.label}
                >
                  {(field.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              ) : (
                <input
                  type={field.kind === "date" ? "date" : "text"}
                  required={field.required}
                  value={values[field.name] ?? ""}
                  onChange={(event) => setValues((current) => ({ ...current, [field.name]: event.currentTarget.value }))}
                  aria-label={field.label}
                />
              )}
            </label>
          ))}
          <button className="primary-button" type="submit" disabled={submitting || missingRequired}>
            {submitting ? "Creating…" : `Save ${entityLabel}`}
          </button>
          {submitError && <p role="alert" className="form-error">{submitError}</p>}
        </form>
      )}
      {listError && <p role="alert" className="form-error">{panelLabel} could not be loaded: {listError}</p>}
      <ul className="live-resource-list">
        {rows.map((row) => <li key={row.id} data-testid="live-resource-row">{renderRow(row)}</li>)}
        {rows.length === 0 && !listError && <li className="inline-empty">{emptyLabel}</li>}
      </ul>
    </section>
  );
}

function LiveRfisPanel({ session, client }: { session: IdentitySessionState; client: LiveResourceClient<RfiRow> }) {
  return (
    <LiveResourcePanel<RfiRow>
      panelLabel="Live RFIs"
      entityLabel="an RFI"
      createButtonLabel="Raise live RFI"
      signedOutButtonLabel="Sign in to raise an RFI"
      idPrefix="rfi:live"
      session={session}
      client={client}
      emptyLabel="No live RFIs yet."
      fields={[
        { name: "subject", label: "Subject", kind: "text", required: true },
        { name: "raisedBySubject", label: "Raised by subject", kind: "text" },
        { name: "ownerSubject", label: "Owner subject", kind: "text" },
        { name: "dueDate", label: "Due date", kind: "date" },
        { name: "status", label: "Status", kind: "select", options: rfiStatuses },
      ]}
      defaultValues={(session) => ({
        subject: "",
        raisedBySubject: session.subject ?? "",
        ownerSubject: "",
        dueDate: "",
        status: "open",
      })}
      buildBody={(values, session, id, nowIso) => ({
        id,
        subject: values.subject.trim(),
        raisedBySubject: values.raisedBySubject.trim() || session.subject || "unassigned",
        ownerSubject: values.ownerSubject.trim() || "unassigned",
        dueDate: values.dueDate.trim() || nowIso.slice(0, 10),
        status: values.status,
        createdAt: nowIso,
      })}
      renderRow={(row) => (
        <>
          <strong>{row.subject}</strong>
          <span>{row.status} · due {row.dueDate}</span>
          <span>Raised by {row.raisedBySubject} · owner {row.ownerSubject}</span>
        </>
      )}
    />
  );
}

function LiveApprovalsPanel({ session, client }: { session: IdentitySessionState; client: LiveResourceClient<ApprovalRow> }) {
  return (
    <LiveResourcePanel<ApprovalRow>
      panelLabel="Live approvals"
      entityLabel="an approval"
      createButtonLabel="Submit live approval"
      signedOutButtonLabel="Sign in to submit an approval"
      idPrefix="approval:live"
      session={session}
      client={client}
      emptyLabel="No live approvals yet."
      fields={[
        { name: "subject", label: "Subject", kind: "text", required: true },
        { name: "package", label: "Package", kind: "text" },
        { name: "submittedBySubject", label: "Submitted by subject", kind: "text" },
        { name: "status", label: "Status", kind: "select", options: approvalStatuses },
      ]}
      defaultValues={(session) => ({
        subject: "",
        package: "",
        submittedBySubject: session.subject ?? "",
        status: "awaiting-review",
      })}
      buildBody={(values, session, id, nowIso) => ({
        id,
        subject: values.subject.trim(),
        package: values.package.trim() || "unspecified",
        submittedBySubject: values.submittedBySubject.trim() || session.subject || "unassigned",
        submittedAt: nowIso,
        status: values.status,
      })}
      renderRow={(row) => (
        <>
          <strong>{row.subject}</strong>
          <span>{row.package} · {row.status.replaceAll("-", " ")}</span>
          <span>Submitted by {row.submittedBySubject}</span>
        </>
      )}
    />
  );
}

function RfisPage({
  fixture,
  session,
  liveRfisClient,
}: {
  fixture: Readonly<CollaborationFixture>;
  session: IdentitySessionState;
  liveRfisClient: LiveResourceClient<RfiRow>;
}) {
  return (
    <>
      <PageHeader eyebrow="Formal questions" title="Requests for information" summary="Fixture correspondence with explicit owner, due date, and linked-record counts." action={<button className="primary-button" type="button" disabled>Raise RFI</button>} />
      <LiveRfisPanel session={session} client={liveRfisClient} />
      <section className="card-list">
        {fixture.rfis.map((rfi) => (
          <article className="workflow-card" key={rfi.id}>
            <div className="workflow-top"><span>{rfi.id}</span><Badge tone={rfi.status}>{rfi.status}</Badge></div>
            <h2>{rfi.subject}</h2>
            <dl><div><dt>Raised by</dt><dd>{rfi.raisedBy}</dd></div><div><dt>Owner</dt><dd>{rfi.owner}</dd></div><div><dt>Due</dt><dd>{formatDate(rfi.dueDate)}</dd></div></dl>
            <footer>{rfi.linkedRecords} linked records <span>View fixture →</span></footer>
          </article>
        ))}
        {fixture.rfis.length === 0 && <div className="empty-state panel">No RFIs in this fixture.</div>}
      </section>
    </>
  );
}

function ApprovalsPage({
  fixture,
  session,
  liveApprovalsClient,
}: {
  fixture: Readonly<CollaborationFixture>;
  session: IdentitySessionState;
  liveApprovalsClient: LiveResourceClient<ApprovalRow>;
}) {
  return (
    <>
      <PageHeader eyebrow="Review workflow" title="Approval inbox" summary="Read-only fixture decisions. No authoritative approval action is available in C0." />
      <LiveApprovalsPanel session={session} client={liveApprovalsClient} />
      <section className="approval-list">
        {fixture.approvals.map((approval) => {
          const progress = (approval.reviewersComplete / approval.reviewersTotal) * 100;
          return (
            <article className="approval-row" key={approval.id}>
              <div className="approval-icon" aria-hidden="true">✓</div>
              <div className="approval-main"><span>{approval.id} · {approval.package}</span><h2>{approval.subject}</h2><small>Submitted by {approval.submittedBy} · {formatDate(approval.submittedAt, true)}</small></div>
              <div className="review-progress"><span>{approval.reviewersComplete} of {approval.reviewersTotal} reviews</span><div><i style={{ width: `${progress}%` }} /></div></div>
              <Badge tone={approval.status}>{approval.status.replaceAll("-", " ")}</Badge>
            </article>
          );
        })}
        {fixture.approvals.length === 0 && <div className="empty-state panel">No approvals in this fixture.</div>}
      </section>
    </>
  );
}

function UnavailableProject({ onReturn }: { onReturn: () => void }) {
  return (
    <div className="unavailable-project">
      <span className="large-glyph">HC</span><span className="eyebrow">Fixture boundary</span>
      <h1>No fixture dataset for this project</h1>
      <p>The project exists in the switcher to exercise navigation, but records are intentionally not inferred from Northbank data.</p>
      <button className="primary-button" type="button" onClick={onReturn}>Return to Northbank fixture</button>
    </div>
  );
}

export function App({
  fixture = c0Fixture,
  identityClient = defaultIdentityClient,
  liveIssuesClient = defaultLiveIssuesClient,
  liveRfisClient = defaultLiveRfisClient,
  liveApprovalsClient = defaultLiveApprovalsClient,
  liveDocumentsClient = defaultLiveDocumentsClient,
}: {
  fixture?: Readonly<CollaborationFixture>;
  identityClient?: IdentityClient;
  liveIssuesClient?: LiveIssuesClient;
  liveRfisClient?: LiveResourceClient<RfiRow>;
  liveApprovalsClient?: LiveResourceClient<ApprovalRow>;
  liveDocumentsClient?: LiveDocumentsClient;
}) {
  const initialUiState = useMemo(() => {
    const persisted = loadUiState();
    const validProjectId = persisted?.projectId
      && fixture.projects.some((candidate) => candidate.id === persisted.projectId)
      ? persisted.projectId
      : fixture.activeProjectId;
    return {
      page: persisted?.page ?? "project",
      projectId: validProjectId,
    };
  }, [fixture.activeProjectId, fixture.projects]);
  const [page, setPage] = useState<Page>(initialUiState.page);
  const [projectId, setProjectId] = useState(initialUiState.projectId);
  const [session, setSession] = useState<IdentitySessionState>({ status: "loading" });
  const [pendingIssueSeed, setPendingIssueSeed] = useState<IssueElementSeed | null>(null);
  const project = fixture.projects.find((candidate) => candidate.id === projectId)!;
  const hasFixture = projectId === fixture.activeProjectId;
  const navigationCounts: Partial<Record<Page, number>> = {
    issues: fixture.issues.filter((issue) => issue.workflowState !== "closed").length,
    approvals: fixture.approvals.filter((approval) => approval.status === "awaiting-review").length,
  };

  const changePage = (next: Page) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Persist UI/session state that stays in the browser (never shared). Draft persistence lives in
  // LiveIssuesPanel; see src/browserStorage.ts for the key-separation rule.
  useEffect(() => {
    saveUiState({ page, projectId });
  }, [page, projectId]);

  const refreshSession = () => {
    identityClient
      .fetchSession()
      .then((result) => setSession(result.active ? { status: "signed-in", subject: result.subject } : { status: "signed-out" }))
      .catch(() => setSession({ status: "signed-out" }));
  };

  // Runs once on mount: picks up an already-signed-in session (either a returning visit, or the
  // redirect back from functions/auth/callback.ts after a fresh sign-in), and tidies up the
  // `?auth=success` marker that redirect leaves in the URL. Does not itself decide whether the
  // session is valid -- that is entirely functions/auth/session.ts's call, made through
  // identityClient (see src/identityClient.ts's header comment on why this never touches the
  // network under `npm run test`).
  useEffect(() => {
    refreshSession();
    if (window.location.search.includes("auth=")) window.history.replaceState(null, "", window.location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreateIssueFromElement = (seed: IssueElementSeed) => {
    setPendingIssueSeed(seed);
    changePage("issues");
  };

  const handleSignOut = () => {
    identityClient.signOut().finally(refreshSession);
  };

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content" tabIndex={0}>Skip to main content</a>
      <aside className="sidebar">
        <div className="brand"><span className="brand-mark">AC</span><span>AEC Collaboration<small>Hub</small></span></div>
        <nav aria-label="Primary navigation">
          {navigation.map((item) => (
            <button type="button" key={item.id} aria-current={page === item.id ? "page" : undefined} onClick={() => changePage(item.id)}>
              <span className="nav-glyph" aria-hidden="true">{item.glyph}</span>{item.label}
              {navigationCounts[item.id] !== undefined && <span className="nav-count">{navigationCounts[item.id]}</span>}
            </button>
          ))}
        </nav>
        <div className="sidebar-footer">
          {session.status === "signed-in" ? (
            <>
              <span className="avatar" aria-hidden="true">ID</span>
              <div>
                <strong>Signed in</strong>
                <small className="mono-truncate">{session.subject}</small>
              </div>
              <button className="sidebar-action" type="button" onClick={handleSignOut}>Sign out</button>
            </>
          ) : (
            <>
              <span className="avatar" aria-hidden="true">KT</span>
              <div><strong>Coordination team</strong><small>Anonymous fixture</small></div>
              <a className="sidebar-action" href="/auth/login">Sign in</a>
            </>
          )}
        </div>
      </aside>

      <div className="workspace">
        <div className="topbar">
          <label className="project-switcher"><span className="project-monogram">{project.code.slice(0, 2)}</span><span><small>Current project</small><select value={projectId} onChange={(event) => setProjectId(event.currentTarget.value)} aria-label="Current project">{fixture.projects.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.code} · {candidate.name}</option>)}</select></span></label>
          <div className="topbar-actions"><span className="fixture-pill"><i /> Fixture only · {fixture.fixtureVersion}</span><button className="icon-button" type="button" aria-label="Notifications">2</button></div>
        </div>
        <main id="main-content" tabIndex={-1}>
          {!hasFixture ? <UnavailableProject onReturn={() => setProjectId(fixture.activeProjectId)} /> : (
            <>
              {page === "project" && <ProjectPage onNavigate={changePage} fixture={fixture} />}
              {page === "federation" && <FederationPage fixture={fixture} session={session} onCreateIssueFromElement={handleCreateIssueFromElement} />}
              {page === "documents" && <DocumentsPage fixture={fixture} session={session} liveDocumentsClient={liveDocumentsClient} />}
              {page === "issues" && (
                <IssuesPage
                  fixture={fixture}
                  session={session}
                  liveIssuesClient={liveIssuesClient}
                  seed={pendingIssueSeed}
                  onConsumeSeed={() => setPendingIssueSeed(null)}
                />
              )}
              {page === "rfis" && <RfisPage fixture={fixture} session={session} liveRfisClient={liveRfisClient} />}
              {page === "approvals" && <ApprovalsPage fixture={fixture} session={session} liveApprovalsClient={liveApprovalsClient} />}
            </>
          )}
        </main>
      </div>
    </div>
  );
}
